import { useState } from "react";
import { Modal, Button, Form, Alert, Row, Col } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import { FaCalendarAlt, FaClock } from "react-icons/fa";

const BookNowModal = ({ show, handleClose, yachtId, yachtName }) => {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    whatsapp: "",
    guest: "",
    event: "",
    booking_date: null,
    booking_time: null,
    message: "",
    useSameNumber: false,
    honeypot: "",
  });

  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);

  const validateForm = () => {
    let newErrors = {};
    if (!formData.fullName.trim()) newErrors.fullName = "Full Name is required";
    if (!formData.email.includes("@")) newErrors.email = "Enter a valid email";
    if (!formData.phone.match(/^\d{9,}$/))
      newErrors.phone = "Enter a valid phone number";

  if (
      !formData.useSameNumber &&
      !formData.whatsapp.match(/^\d{9,}$/)
  )


    if (!formData.guest.match(/^\d+$/)) newErrors.guest = "Enter a valid number of guests";
    if (!formData.event.trim()) newErrors.event = "Event type is required";
    if (!formData.booking_date) newErrors.booking_date = "Select a booking date";
    if (!formData.booking_time) newErrors.booking_time = "Select a booking time";
    if (!formData.message.trim()) newErrors.message = "Message cannot be empty";
    if (formData.honeypot) newErrors.honeypot = "Bot detected!";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => {
      const updatedForm = {
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      };

      // If "Use same number" is checked, update WhatsApp number automatically
      if (name === "useSameNumber") {
        updatedForm.whatsapp = checked ? prev.phone : "";
      }
      if (name === "phone" && prev.useSameNumber) {
        updatedForm.whatsapp = value;
      }

      return updatedForm;
    });

    setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
  };

  const handleDateChange = (date) => {
    setFormData((prev) => ({ ...prev, booking_date: date }));
    setErrors((prevErrors) => ({ ...prevErrors, booking_date: "" }));
  };

  // Handle Time Change
  const handleTimeChange = (time) => {
    setFormData((prev) => ({ ...prev, booking_time: time }));
    setErrors((prevErrors) => ({ ...prevErrors, booking_time: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    const apiUrl = `${process.env.REACT_APP_SERVER_URL}api/bookings/create/`;
    const authHeader = `Basic ${btoa(`${process.env.REACT_APP_USERNAME}:${process.env.REACT_APP_PASSWORD}`)}`;

    const formattedDate = moment(formData.booking_date).format("YYYY-MM-DD");
    const formattedTime = moment(formData.booking_time, "HH:mm").format("hh:mm A");

    const bookingData = {
      yacht: yachtId,
      name: formData.fullName,
      email: formData.email,
      mobile: formData.phone,
      whatsapp: formData.whatsapp,
      guest: formData.guest,
      event: formData.event,
      booking_date: moment(formData.booking_date).format("YYYY-MM-DD"),
      booking_time: moment(formData.booking_time).format("hh:mm A"),
      message: formData.message,
    };

    console.log("Submitting Booking Data:", bookingData); 

    try {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: authHeader,
        },
        body: JSON.stringify(bookingData),
      });

      if (!response.ok) {
        throw new Error("Booking failed. Please try again.");
      }

      setSubmitted(true);
      setFormData({
        fullName: "",
        email: "",
        phone: "",
        whatsapp: "",
        guest: "",
        event: "",
        booking_date: null,
        booking_time: null,
        message: "",
        useSameNumber: false,
        honeypot: "",
      });

      alert("Booking Submitted Successfully!"); // Confirmation message
      handleClose();
    } catch (error) {
      console.error("Error submitting booking:", error);
      setErrors({ apiError: error.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Book Now</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {submitted && <Alert variant="success">Booking Submitted Successfully!</Alert>}
        {errors.apiError && <Alert variant="danger">{errors.apiError}</Alert>}
        {errors.honeypot && <Alert variant="danger">Bot detected! Submission blocked.</Alert>}

        <Form onSubmit={handleSubmit}>
          {/* Yacht Name (Read-Only) */}
          <Form.Group className="form-floating mb-4">
            <Form.Control type="text" value={yachtName} readOnly placeholder="Yacht Name" />
            <Form.Label>Yacht Name</Form.Label>
          </Form.Group>

          <Row className="mb-md-4 mb-3">
            <Col md={6}>
              <Form.Group className="form-floating">
                <Form.Control
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  isInvalid={!!errors.fullName}
                  placeholder="Full Name"
                />
                <Form.Label>Full Name</Form.Label>
                <Form.Control.Feedback type="invalid">{errors.fullName}</Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6} className="mt-md-0 mt-3">
              <Form.Group className="form-floating">
                <Form.Control
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  isInvalid={!!errors.email}
                  placeholder="Email"
                />
                <Form.Label>Email</Form.Label>
                <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-md-4 mb-3">
            <Col md={6}>
              <Form.Group className="form-floating">
                <Form.Control
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  isInvalid={!!errors.phone}
                  placeholder="Phone Number With Country Code"
                />
                <Form.Label>Phone Number</Form.Label>
                <Form.Control.Feedback type="invalid">{errors.phone}</Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6} className="mt-md-0 mt-3">
              <Form.Group className="form-floating">
                <Form.Control
                  type="tel"
                  name="whatsapp"
                  value={formData.whatsapp}
                  onChange={handleChange}
                  isInvalid={!!errors.whatsapp}
                  placeholder="WhatsApp Number With Country Code"
                  disabled={formData.useSameNumber}
                />
                <Form.Label>WhatsApp Number</Form.Label>
                <Form.Control.Feedback type="invalid">{errors.whatsapp}</Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-4">
            <Form.Check
              type="checkbox"
              label="Use the same phone number for WhatsApp"
              name="useSameNumber"
              checked={formData.useSameNumber}
              onChange={handleChange}
            />
          </Form.Group>

          <Row className="mb-md-4 mb-3">
            <Col md={6}>
              <Form.Group className="form-floating">
                <Form.Control
                  type="number"
                  name="guest"
                  value={formData.guest}
                  onChange={handleChange}
                  isInvalid={!!errors.guest}
                  placeholder="Number of Guests"
                />
                <Form.Label>Number of Guests</Form.Label>
                <Form.Control.Feedback type="invalid">{errors.guest}</Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6} className="mt-md-0 mt-3">
              <Form.Group className="form-floating">
                <Form.Control
                  type="text"
                  name="event"
                  value={formData.event}
                  onChange={handleChange}
                  isInvalid={!!errors.event}
                  placeholder="Event Type"
                />
                <Form.Label>Event Type</Form.Label>
                <Form.Control.Feedback type="invalid">{errors.event}</Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          {/* Date & Time Pickers */}
          <Row className="mb-md-4 mb-3">
            <Col md={6}>
              <Form.Group>
                {/* <Form.Label className="custom-label">Booking Date</Form.Label> */}
                <DatePicker
                  selected={formData.booking_date}
                  onChange={handleDateChange}
                  className="form-control custom-datepicker"
                  dateFormat="yyyy-MM-dd"
                  placeholderText="Booking Date"
                />
                <Form.Control.Feedback type="invalid">{errors.booking_date}</Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6} className="mt-md-0 mt-3">
              <Form.Group>
                {/* <Form.Label className="custom-label">Booking Time</Form.Label> */}
                <DatePicker
                  selected={formData.booking_time}
                  onChange={handleTimeChange}
                  showTimeSelect
                  showTimeSelectOnly
                  timeIntervals={15}
                  timeCaption="Time"
                  dateFormat="h:mm aa"
                  className="form-control custom-datepicker"
                  placeholderText="Booking Time"
                />
                <Form.Control.Feedback type="invalid">{errors.booking_time}</Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="form-floating mb-4">
            <Form.Control
              as="textarea"
              rows={3}
              name="message"
              className="form-control"
              value={formData.message}
              onChange={handleChange}
              isInvalid={!!errors.message}
              placeholder="Message"
            />
            <Form.Label>Message</Form.Label>
            <Form.Control.Feedback type="invalid">{errors.message}</Form.Control.Feedback>
          </Form.Group>

          {/* Hidden Honeypot Input (Bot Check) */}
          <input
            type="text"
            name="honeypot"
            value={formData.honeypot}
            onChange={handleChange}
            style={{ display: "none" }}
            tabIndex="-1"
            autoComplete="off"
          />

          <Modal.Footer>
            <Button
              type="submit"
              style={{
                backgroundColor: "#1c2e4a",
                padding: "10px 30px",
                color: "#fff",
                border: "none",
                borderRadius: "5px",
              }}
              disabled={loading}
            >
              {loading ? "Submitting..." : "Submit"}
            </Button>
          </Modal.Footer>
        </Form>

      </Modal.Body>
    </Modal>
  );
};

export default BookNowModal;
